import { UploadOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, message, Modal, Row, Switch, Upload, UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadVideoInterface } from 'type/uploadVideoType';
import * as Component from '../../../shared/components';
import UploadVideoList from './components/UploadVideoList';
import { uploadVideoService } from './service/UploadVideoService';
import { uploadVideoStore } from './store/uploadVideoStore';
import './UploadVideo.css';

const UploadVideoPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const uploadVideoList = async (): Promise<void> => {
    const uploadVideoList = uploadVideoStore.getUploadVideoList();
    const needUploadVideoList = uploadVideoList.filter(
      (videoFile: UploadVideoInterface) =>
        videoFile.state === 'normal' || videoFile.state === 'failed'
    );
    const autoTranscode = form.getFieldValue('autoTranscode');

    if (needUploadVideoList.length === 0) {
      message.warning('No videos selected for upload.');
    } else {
      const uploadResult = await uploadVideoService.uploadVideoFiles(
        needUploadVideoList,
        autoTranscode
      );

      if (uploadResult) {
        uploadVideoStore.clearUploadVideoList();
        navigate('/library');
      }
    }
  };

  const onClickUploadButton = async (): Promise<void> => {
    const videoList = uploadVideoStore.getUploadVideoList();
    const failedUploadList = videoList.filter(
      (videoFile: UploadVideoInterface) => videoFile.state === 'failed'
    );

    if (failedUploadList.length > 0) {
      Modal.confirm({
        title: 'Retry Upload',
        content:
          'There is a failed upload. Retry upload? Include failed upload in the upload list.',
        onOk: () => {
          uploadVideoList();
        },
      });
    } else {
      uploadVideoList();
    }
  };

  return (
    <Component.BasePageComponent>
      <div className="upload-video-page">
        <div className="upload-header" style={{ marginBottom: '10px' }}>
          <Component.CustomButtonComponent
            icon={<UploadOutlined />}
            text="Upload"
            onClick={onClickUploadButton}
          />
        </div>
        <Row gutter={16}>
          <Col span={16}>
            <Form form={form} layout="vertical" className="upload-video-form">
              <Form.Item name="autoTranscode">
                <Switch checkedChildren="Auto Transcode" unCheckedChildren="Auto Transcode" />
              </Form.Item>
              <Upload
                multiple
                accept="video/*"
                onChange={(info: UploadChangeParam<UploadFile<UploadVideoInterface>>) =>
                  uploadVideoStore.setUploadVideoList(info)
                }
                beforeUpload={() => false}
                fileList={uploadVideoStore.getUploadVideoList()}
                showUploadList={false}
              >
                <Button style={{ marginBottom: '16px' }} icon={<UploadOutlined />}>
                  Select File
                </Button>
              </Upload>
              <UploadVideoList />
              <p>
                Get started by selecting a video file to upload. After your upload is finished you
                can add metadata to your video.
              </p>
            </Form>
          </Col>
          <Col span={8}>
            <Card title="Transcode Profile Details" className="transcode-profile-card">
              <p>The selected transcode profile is configured with the following settings:</p>
              <ul>
                <li>HLS 1080p (Medium)</li>
                <li>HLS 720p (Medium)</li>
                <li>HLS 480p (Medium)</li>
                <li>HLS 360p (Medium)</li>
              </ul>
            </Card>
          </Col>
        </Row>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(UploadVideoPage);
